import React, { useEffect, useRef, useState } from 'react'
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core'
import { Link } from 'react-router-dom'
import { send_command, ADMIN_GET_APP_CLIENTS } from 'components/api'
import { AppClient } from '../AppClient'
import { ClientEmails } from '../ClientEmails'
import { ClientLogs } from '../ClientLogs'
import { ClientStatusBanner } from '../components/ClientStatusBanner'
import { RemoteConsole } from '../components/RemoteConsole'
import { useTenant } from '../context/TenantContext'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'
import { formatUtcTimeAgo } from 'utils/formatUtcTimestamp'

function SectionLabel({ children }) {
    return <div className="sa-section-label">{children}</div>
}

function DetailField({ label, value, mono = false, onClick, title }) {
    return (
        <div className="superadmin-detail-field">
            <div className="superadmin-detail-field__label">{label}</div>
            <div
                className={[
                    'superadmin-detail-field__value',
                    mono ? 'is-mono' : '',
                    onClick ? 'is-clickable' : '',
                ].filter(Boolean).join(' ')}
                onClick={onClick}
                title={title}
            >
                {value}
            </div>
        </div>
    )
}

function SingleClientDetail({ client }) {
    const [secret, setSecret] = useState('***')
    const secretTimeout = useRef()
    const [showSpids, setShowSpids] = useState(false)

    useEffect(() => {
        return () => { if (secretTimeout.current) clearTimeout(secretTimeout.current) }
    }, [])

    const fetchSecret = () => {
        send_command('ADMIN_GET_APP_CLIENT_SECRET', { app_client_id: client.app_client_id })
            .then(res => {
                setSecret(res.key)
                if (secretTimeout.current) clearTimeout(secretTimeout.current)
                secretTimeout.current = setTimeout(() => setSecret('***'), 10000)
            })
            .catch(() => setSecret('could not decode'))
    }

    const clientSpidList = client.client_spids
        ? (Array.isArray(client.client_spids)
            ? client.client_spids
            : Object.values(client.client_spids))
        : null

    const downloadSpids = () => {
        if (!clientSpidList) return
        const blob = new Blob([JSON.stringify(clientSpidList, null, 2)])
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.download = 'client_spids.json'
        link.href = url
        link.click()
    }

    const version = client.version
        ? (typeof client.version === 'object' ? JSON.stringify(client.version) : client.version)
        : null

    const spidsSynced = client.client_spid_count != null
        ? `${client.client_spid_count}${client.client_spids_updated_at
            ? ` · ${formatUtcTimeAgo(client.client_spids_updated_at)}`
            : ''}`
        : '—'

    return (
        <>
            <div className="superadmin-detail-grid">
                <DetailField label="Name" value={client.app_client_name || client.tenant || '—'} />
                <DetailField label="App Client ID" value={client.app_client_id} mono />
                {version && <DetailField label="Version" value={version} mono />}
                <DetailField label="SPIDs synced to client" value={spidsSynced} />
                {client.command_state && (
                    <DetailField label="Last command state" value={client.command_state} />
                )}
                <DetailField
                    label="Secret"
                    value={secret}
                    mono
                    onClick={fetchSecret}
                    title="Click to reveal — hides after 10 seconds"
                />
            </div>

            <div className="superadmin-toolbar">
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowSpids(v => !v)}
                    disabled={!clientSpidList}
                >
                    {showSpids ? 'Hide SPIDs' : `View SPIDs${clientSpidList ? ` (${clientSpidList.length})` : ''}`}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={downloadSpids}
                    disabled={!clientSpidList}
                >
                    Download SPIDs
                </Button>
            </div>

            {!clientSpidList && (
                <Typography variant="body2" color="textSecondary" className="superadmin-muted-note">
                    No client SPID snapshot — run Sync SPIDs from Remote Control to populate.
                </Typography>
            )}

            {showSpids && clientSpidList && (
                <Table size="small" className="superadmin-data-table">
                    <TableHead>
                        <TableRow>
                            <TableCell>SPID</TableCell>
                            <TableCell>State</TableCell>
                            <TableCell>Created</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {clientSpidList.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Typography variant="body2" color="textSecondary">No SPIDs in snapshot</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {clientSpidList.map(s => (
                            <TableRow key={s.sp_id}>
                                <TableCell className="is-mono">{s.sp_id}</TableCell>
                                <TableCell>{s.state}</TableCell>
                                <TableCell>{s.created_at || s.created_time || '—'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </>
    )
}

export function TenantClientAppsTab() {
    const { tenantId } = useTenant()
    const [client, setClient] = useState(null)
    const [clientLoading, setClientLoading] = useState(true)
    const [clientError, setClientError] = useState(null)
    const [emailsRefreshToken, setEmailsRefreshToken] = useState(0)

    useEffect(() => {
        setClient(null)
        setClientLoading(true)
        setClientError(null)
        ADMIN_GET_APP_CLIENTS(tenantId)
            .then(clients => {
                setClient(Array.isArray(clients) && clients.length > 0 ? clients[0] : null)
                setClientLoading(false)
            })
            .catch(ex => {
                setClientError(getApiErrorMessage(ex, 'Failed to load client app'))
                setClientLoading(false)
            })
    }, [tenantId])

    const overviewPath = `/superadmin/tenants/${tenantId}`

    return (
        <div className="superadmin-tenant-client-app">
            <ClientStatusBanner
                client={client}
                clientLoading={clientLoading}
                variant="client-app"
            />

            <div className="superadmin-card superadmin-section-panel">
                <SectionLabel>App client</SectionLabel>
                {clientLoading && (
                    <Typography variant="body2" color="textSecondary">Loading…</Typography>
                )}
                {!clientLoading && clientError && (
                    <Typography variant="body2" color="error">{clientError}</Typography>
                )}
                {!clientLoading && !clientError && !client && (
                    <Typography variant="body2" color="textSecondary">No client app configured for this tenant.</Typography>
                )}
                {!clientLoading && client && (
                    <SingleClientDetail client={client} />
                )}
            </div>

            <div className="superadmin-card superadmin-section-panel">
                <SectionLabel>Remote Control</SectionLabel>
                {client ? (
                    <RemoteConsole
                        tenantId={tenantId}
                        appClientId={client.app_client_id}
                        onEmailsRetrieved={() => setEmailsRefreshToken(t => t + 1)}
                    />
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        {clientLoading ? 'Loading…' : 'No client app available'}
                    </Typography>
                )}
            </div>

            <Typography variant="body2" color="textSecondary" className="superadmin-muted-note">
                Client keys are managed on the <Link to={overviewPath}>Overview</Link> tab.
            </Typography>

            <div className="superadmin-card superadmin-section-panel">
                <SectionLabel>Logs</SectionLabel>
                <ClientLogs tenant_id={tenantId} />
            </div>

            <div className="superadmin-card superadmin-section-panel">
                <SectionLabel>Client emails</SectionLabel>
                <ClientEmails tenant_id={tenantId} refreshToken={emailsRefreshToken} />
            </div>

            <div className="superadmin-card superadmin-section-panel superadmin-client-app-tools">
                <SectionLabel>App Client ID Converter</SectionLabel>
                <Typography variant="body2" color="textSecondary" className="superadmin-muted-note">
                    Developer utility for encoding Cognito app client credentials into an environment file.
                </Typography>
                <AppClient />
            </div>
        </div>
    )
}
